import { useCallback, useEffect, useState } from "react";
import { redirectIfPhonePe } from "../lib/payRail";
import { PayRailButtons } from "../components/payments/PayRailButtons";
import { toast, Toaster } from "sonner";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Crown, Loader2, ArrowLeft, ArrowRight, ShieldCheck, Download, MapPin,
  Building2, Truck, Trophy, BadgeCheck, Lock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

export default function BigCapLeague() {
  const { user, api, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get("/big-cap/overview");
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [api, user]);

  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  const my = data?.my_state;
  const th = data?.thresholds;
  const isMember = my?.status === "inducted";
  const isReviewing = my?.status === "under_scrutiny";
  const depositPaid = my?.deposit?.paid;

  const payDeposit = async (railChoice = "razorpay") => {
    setBusy(railChoice);
    try {
      const r = await api.post(`/big-cap/security-deposit/create-order?rail=${railChoice}`);
      if (redirectIfPhonePe(r.data)) return;
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      const opts = {
        key: r.data.key_id, amount: r.data.amount * 100, currency: r.data.currency,
        order_id: r.data.order_id,
        name: "Vinci Big-Cap League",
        description: `Refundable security deposit · ${r.data.lockin_months}-month lock-in`,
        prefill: r.data.prefill,
        theme: { color: "#D8B23E" },
        handler: async (rp) => {
          try {
            await api.post("/big-cap/security-deposit/verify", {
              razorpay_order_id: rp.razorpay_order_id,
              razorpay_payment_id: rp.razorpay_payment_id,
              razorpay_signature: rp.razorpay_signature,
            });
            toast.success("Deposit locked · scrutiny complete");
            await refresh(); load();
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      new window.Razorpay(opts).open();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment start failed"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const r = await api.get("/big-cap/certificate/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "vinci-bigcap-induction.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Certificate not ready"); }
  };

  return (
    <div className={`min-h-screen text-[#F5F5F0] ${isMember ? "bg-gradient-to-b from-[#0a0704] via-[#050506] to-[#050506]" : "bg-[#050506]"}`}>
      <Toaster theme="dark" richColors position="top-right" />
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <Link to="/account" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] mb-8">
          <ArrowLeft size={12}/> Back to account
        </Link>

        {/* Monumental hero */}
        <div className="relative overflow-hidden border border-[#D8B23E]/30 rounded-sm bg-gradient-to-br from-[#141414] via-black to-[#0a0a0a] p-8 md:p-12">
          <motion.div aria-hidden className="absolute -inset-32 rounded-full opacity-25 blur-3xl"
            animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
            transition={{ duration: 18, repeat: Infinity }}
            style={{ background: "radial-gradient(circle at 20% 30%, #D8B23E33, transparent 55%), radial-gradient(circle at 80% 70%, #B8733333, transparent 55%)" }}/>
          <div className="relative">
            <div className="inline-flex items-center gap-2 border border-[#D8B23E]/50 rounded-full px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">
              <Crown size={11}/> Big-Cap Infrastructure League · Executive Access
            </div>
            <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
              Nation-scale contracts. Vinci's most guarded tier.
            </h1>
            <p className="font-body text-sm md:text-base text-[#8A8A82] mt-4 max-w-3xl">
              Priority allocation on Vinci's flagship contracts — metro, expressway, high-speed rail and smart-city
              tenders. Entry is by executive scrutiny and a 36-month deposit lock-in only.
            </p>

            <MyStateCard my={my} th={th} isMember={isMember} depositPaid={depositPaid}
                          isReviewing={isReviewing} onPayDeposit={payDeposit}
                          onDownload={download} busy={busy}/>
          </div>
        </div>

        {loading ? (
          <div className="mt-8 text-center font-body text-sm text-[#8A8A82] py-8">
            <Loader2 size={16} className="inline animate-spin mr-2"/> Loading league…
          </div>
        ) : (
          <>
            {/* League requirements */}
            <section className="mt-12">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">League Requirements</div>
                  <h2 className="font-serif text-3xl mt-1">Four barriers · no exceptions.</h2>
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-3">
                <Requirement icon={<Building2 size={16}/>} label="Financial Power"
                  value={`${(th?.turnover_inr || 0) / 1_00_00_000} Cr`}
                  sub="Minimum annual audited turnover"/>
                <Requirement icon={<Truck size={16}/>} label="Infrastructure"
                  value={`${(th?.storage_mt || 0).toLocaleString("en-IN")} MT`}
                  sub="Minimum owned storage capacity"/>
                <Requirement icon={<Trophy size={16}/>} label="Loyalty"
                  value={`${th?.trades || 0}+ trades`}
                  sub="Successful trades on Vinci network"/>
                <Requirement icon={<Lock size={16}/>} label="Security"
                  value={inr(th?.deposit_inr || 0)}
                  sub={`Refundable · ${th?.lockin_months || 36}-month lock-in`}/>
              </div>
            </section>

            {/* Active projects */}
            <section className="mt-16">
              <div className="mb-6">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Active Big-Cap Projects</div>
                <h2 className="font-serif text-3xl mt-1">Three flagship contracts open now.</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {(data?.projects || []).map((p, idx) => (
                  <ProjectTile key={p.id} project={p} idx={idx}/>
                ))}
              </div>
            </section>

            {/* Track record */}
            <section className="mt-16">
              <div className="mb-6">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Executed by Vinci</div>
                <h2 className="font-serif text-3xl mt-1">Recent track record.</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                {(data?.track_record || []).map((t) => (
                  <div key={t.id} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#B87333]">Delivered {t.delivered_year}</div>
                    <div className="font-serif text-lg text-[#F5F5F0] mt-1">{t.name}</div>
                    <div className="font-body text-sm text-[#C9C9C2] mt-2">{t.detail}</div>
                    <div className="font-body text-[11px] text-[#8A8A82] mt-3">Duration · {t.duration_months} months</div>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            {!isMember && (
              <section className="mt-16 border border-[#D8B23E]/40 bg-[#D8B23E]/[0.04] rounded-sm p-8 md:p-10 text-center">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Ready for scrutiny?</div>
                <h2 className="font-serif text-3xl mt-2">Fill the Elite Certification Form.</h2>
                <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl mx-auto">
                  10+ mandatory fields: audited balance sheets, logistics fleet, ISO certifications, delivered projects.
                  Vinci leadership decides in 10 business days.
                </p>
                {isReviewing ? (
                  <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-[#B87333]/60 text-[#B87333] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
                    <Loader2 size={11} className="animate-spin"/> Under Executive Scrutiny
                  </div>
                ) : (
                  <Link to="/league/apply" data-testid="apply-league-btn"
                    className="mt-6 inline-flex items-center gap-2 rounded-sm bg-[#D8B23E] hover:bg-[#c5a233] text-black font-grotesk text-[11px] uppercase tracking-[0.28em] px-6 py-3">
                    Begin Elite Certification <ArrowRight size={12}/>
                  </Link>
                )}
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}


function MyStateCard({ my, th, isMember, depositPaid, isReviewing, onPayDeposit, onDownload, busy }) {
  if (!my) return null;
  if (isMember) {
    return (
      <div className="mt-8 inline-flex flex-wrap items-center gap-3 border border-[#D8B23E]/60 rounded-sm bg-black/40 px-4 py-3" data-testid="member-state">
        <span className="inline-flex items-center gap-1.5 text-[#D8B23E] font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <ShieldCheck size={12}/> Member · {my.membership_no}
        </span>
        <span className="font-body text-[11px] text-[#8A8A82]">
          Deposit lock: {my.deposit?.lock_days_left != null ? `${my.deposit.lock_days_left} days left` : "—"}
        </span>
        <button onClick={onDownload} data-testid="download-induction"
          className="inline-flex items-center gap-1.5 rounded-sm bg-[#D8B23E] hover:bg-[#c5a233] text-black px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Download size={11}/> Certificate
        </button>
      </div>
    );
  }
  if (isReviewing) {
    return (
      <div className="mt-8 flex flex-wrap items-center gap-4" data-testid="under-scrutiny-state">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#B87333]/60 text-[#B87333] px-3 py-1 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Loader2 size={11} className="animate-spin"/> Under Executive Scrutiny
        </span>
        {!depositPaid && (
          <div className="w-full max-w-sm" data-testid="pay-deposit-btn">
            <PayRailButtons testId="pay-deposit" compact onPay={onPayDeposit} busy={busy}
                            amountLabel={`₹${th?.deposit_inr.toLocaleString("en-IN")} deposit`}
                            note="Security deposit locks the moment either rail confirms." />
          </div>
        )}
        {depositPaid && (
          <span className="font-body text-[11px] text-[#3DBB84]">
            <BadgeCheck size={11} className="inline"/> Deposit on file — awaiting decision
          </span>
        )}
      </div>
    );
  }
  return null;
}


function Requirement({ icon, label, value, sub }) {
  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
      <div className="flex items-center gap-2 text-[#D8B23E]">
        {icon}
        <span className="font-grotesk text-[9px] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <div className="font-serif text-3xl text-[#F5F5F0] mt-2">{value}</div>
      <div className="font-body text-[11px] text-[#8A8A82] mt-1">{sub}</div>
    </div>
  );
}


function ProjectTile({ project, idx }) {
  const tonnage = Number(project.tonnage_value || 0).toLocaleString("en-IN");
  return (
    <motion.article data-testid={`bigcap-project-${idx}`}
      whileHover={{ y: -3 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className="relative overflow-hidden rounded-sm border border-[#D8B23E]/25 bg-gradient-to-br from-[#0f0f0f] via-[#0a0a0a] to-black">
      {/* VIP badge */}
      <motion.span aria-hidden
        className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 rounded-full border border-[#D8B23E]/70 bg-black/60 backdrop-blur px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]"
        animate={{ boxShadow: [
          "0 0 0 rgba(216,178,62,0.0)",
          "0 0 22px rgba(216,178,62,0.55)",
          "0 0 0 rgba(216,178,62,0.0)",
        ]}}
        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}>
        <Crown size={10}/> VIP
      </motion.span>

      {/* Monumental band */}
      <div className={`h-24 bg-gradient-to-br ${project.hero_gradient} border-b border-white/5 flex items-end p-4`}>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#D8B23E]">{project.kicker}</div>
      </div>

      <div className="p-5">
        <div className="font-serif text-xl text-[#F5F5F0] leading-tight">{project.name}</div>
        <div className="font-body text-[11px] text-[#8A8A82] mt-1 inline-flex items-center gap-1.5">
          <MapPin size={10}/> {project.location}
        </div>

        {/* Big tonnage number */}
        <div className="mt-5">
          <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">Contract volume</div>
          <div className="font-serif text-5xl text-[#F5F5F0] leading-none mt-1"
               style={{ textShadow: "0 0 24px rgba(216,178,62,0.35)" }}>
            {tonnage}
          </div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E] mt-1">{project.tonnage_unit}</div>
          <div className="font-body text-[11px] text-[#C9C9C2] mt-2">{project.material_line}</div>
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] font-body">
          <div className="border border-white/10 rounded-sm p-2 bg-black/30">
            <div className="text-[#8A8A82] font-grotesk uppercase tracking-[0.2em]">RERA</div>
            <div className="text-[#F5F5F0] font-mono mt-0.5">{project.rera_id}</div>
          </div>
          <div className="border border-white/10 rounded-sm p-2 bg-black/30">
            <div className="text-[#8A8A82] font-grotesk uppercase tracking-[0.2em]">Duration</div>
            <div className="text-[#F5F5F0] mt-0.5">{project.duration_months} months</div>
          </div>
          <div className="border border-white/10 rounded-sm p-2 bg-black/30">
            <div className="text-[#8A8A82] font-grotesk uppercase tracking-[0.2em]">Contract</div>
            <div className="text-[#F5F5F0] mt-0.5">{inr(project.estimated_contract_value_inr)}</div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
